// GenericTable.tsx : Composant de table générique avec support des actions et filtres

import React, { useState, useEffect, useRef } from 'react';
import { MainButton, DeleteButton } from '../../../common';
import { Skeleton } from '@radix-ui/themes';
import * as Tooltip from '@radix-ui/react-tooltip';
import {
	faPen,
	faTrashAlt,
	faBroom,
	faCircleInfo,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

interface Column<T> {
	label: string;
	render: (item: T) => React.ReactNode;
	linkUrl?: (item: T) => string;
	field?: string;
	filterType?: 'text' | 'date';
	/** Infobulle affichée au survol du rond « i » à droite du libellé */
	headerTooltip?: string;
	/** Infobulle native affichée au survol de la cellule */
	cellTitle?: (item: T) => string | undefined;
}

export type Action<T> = {
	type?: 'edit' | 'delete' | 'manage' | 'custom';
	label: string;
	onClick: (item: T) => void;
	confirmMessage?: (item: T) => string;
};

interface GenericTableProps<T> {
	items: T[];
	columns: Column<T>[];
	actions?: Action<T>[];
	filters?: Record<string, string>;
	onFiltersChange?: (filters: Record<string, string>) => void;
	onSortChange?: (field: string, order: 'ASC' | 'DESC') => void;
	sortField?: string;
	sortOrder?: 'ASC' | 'DESC';
	emptyMessage?: string;
	loading?: boolean;
	size?: 'sm' | 'md' | 'lg';
}

export function GenericTable<T extends { id?: number }>({
	items,
	columns,
	actions = [],
	filters = {},
	onFiltersChange,
	onSortChange,
	sortField,
	sortOrder,
	emptyMessage = 'Aucun élément trouvé.',
	loading = false,
	size = 'md',
}: GenericTableProps<T>) {
	const hideIcons = size !== 'sm';
	const [localFilters, setLocalFilters] = useState<Record<string, string>>(
		filters as Record<string, string>
	);

	// Ref pour debounce
	const debounceRef = useRef<NodeJS.Timeout | null>(null);

	const handleFilterChange = (key: string | number, value: string) => {
		setLocalFilters((prev) => ({ ...prev, [key]: value }));

		if (debounceRef.current) clearTimeout(debounceRef.current);
		debounceRef.current = setTimeout(() => {
			onFiltersChange?.({ ...localFilters, [key]: value });
		}, 300);
	};

	const clearFilters = () => {
		if (debounceRef.current) clearTimeout(debounceRef.current);
		setLocalFilters({});
		onFiltersChange?.({});
	};
	const hasFilters = Object.values(localFilters).some((value) => value);

	useEffect(
		() => () => {
			if (debounceRef.current) clearTimeout(debounceRef.current);
		},
		[]
	);

	return (
		<div className="overflow-x-auto">
			<table className="w-full border border-gray-200 rounded-lg table-auto">
				<thead className="bg-gray-50">
					<tr>
						{columns.map((col, idx) => (
							<th
								key={idx}
								className={`px-6 py-3 text-left text-gray-700 font-medium border-b select-none ${
									col.field !== 'none' ? 'cursor-pointer' : ''
								}`}
								onClick={() => {
									if (col.field && col.field !== 'none') {
										let newOrder: 'ASC' | 'DESC' = 'ASC';
										if (sortField === col.field)
											newOrder = sortOrder === 'ASC' ? 'DESC' : 'ASC';
										onSortChange?.(col.field, newOrder);
									}
								}}
							>
								<div className="flex items-center gap-1.5">
									{col.label}
									{col.headerTooltip && (
										<Tooltip.Provider delayDuration={200}>
											<Tooltip.Root>
												<Tooltip.Trigger asChild>
													<button
														type="button"
														className="inline-flex text-gray-300 hover:text-gray-400"
														aria-label="Information"
														onClick={(event) => event.stopPropagation()}
													>
														<FontAwesomeIcon
															icon={faCircleInfo}
															className="w-3.5 h-3.5"
														/>
													</button>
												</Tooltip.Trigger>
												<Tooltip.Content
													side="top"
													align="start"
													className="z-50 max-w-xs rounded bg-gray-800 px-2 py-1 text-xs text-white"
												>
													{col.headerTooltip}
													<Tooltip.Arrow className="fill-gray-800" />
												</Tooltip.Content>
											</Tooltip.Root>
										</Tooltip.Provider>
									)}
									{col.field !== 'none' && sortField === col.field && (
										<span>{sortOrder === 'ASC' ? '▲' : '▼'}</span>
									)}
								</div>
							</th>
						))}
						{actions.length > 0 && (
							<th className="px-6 py-3 text-left text-gray-700 font-medium border-b">
								Actions
							</th>
						)}
					</tr>

					{/* Ligne de filtre */}
					<tr className="bg-gray-50">
						{columns.map((col, idx) => (
							<td
								key={idx}
								className={`py-2 align-top ${col.filterType === 'date' ? 'px-0 pl-6' : 'px-6'}`}
							>
								{col.field !== 'none' && col.filterType === 'date' ? (
									<div className="flex flex-col gap-1">
										{/* Label "Du" + input date début */}
										<label className="flex items-center gap-1">
											<input
												type="date"
												value={localFilters[`${idx}_from`] || ''}
												onChange={(e) =>
													handleFilterChange(`${idx}_from`, e.target.value)
												}
												className="w-24 text-xs border-b border-gray-300 focus:border-indigo-500 focus:outline-none bg-transparent italic text-gray-700"
											/>
										</label>
										{/* Label "Au" + input date fin */}
										<label className="flex items-center gap-1">
											<input
												type="date"
												value={localFilters[`${idx}_to`] || ''}
												onChange={(e) =>
													handleFilterChange(`${idx}_to`, e.target.value)
												}
												className="w-24 text-xs border-b border-gray-300 focus:border-indigo-500 focus:outline-none bg-transparent italic text-gray-700"
											/>
										</label>
									</div>
								) : (
									<>
										{col.field !== 'none' && (
											<input
												type="text"
												value={localFilters[idx] || ''}
												onChange={(e) =>
													handleFilterChange(idx, e.target.value)
												}
												className=" w-full text-sm border-b border-gray-300 focus:border-indigo-500 focus:outline-none bg-transparent align-top italic text-gray-700"
												placeholder="Filtrer..."
											/>
										)}
									</>
								)}
							</td>
						))}
						{actions.length > 0 && (
							<td className="px-3 py-2 text-right align-top">
								{hasFilters && (
									<button
										type="button"
										title="Effacer les filtres"
										onClick={clearFilters}
										className="text-gray-400 hover:text-gray-600"
									>
										<FontAwesomeIcon icon={faBroom} className="w-5 h-5" />
									</button>
								)}
							</td>
						)}
					</tr>
				</thead>

				<tbody className="bg-white divide-y divide-gray-200">
					{loading ? (
						Array.from({ length: 5 }).map((_, rowIdx) => (
							<tr key={rowIdx}>
								{columns.map((_, colIdx) => (
									<td key={colIdx} className="px-6 py-4">
										<Skeleton className="h-4 w-full" />
									</td>
								))}
								{actions.length > 0 && (
									<td className="px-6 py-4">
										<Skeleton className="h-4 w-16" />
									</td>
								)}
							</tr>
						))
					) : items.length === 0 ? (
						<tr>
							<td
								colSpan={columns.length + (actions.length > 0 ? 1 : 0)}
								className="px-6 py-4 text-sm text-gray-500"
							>
								{emptyMessage}
							</td>
						</tr>
					) : (
						items.map((item, idx) => (
							<tr key={item.id ?? idx}>
								{columns.map((col, cidx) => (
									<td
										key={cidx}
										className="px-6 py-4 text-sm text-gray-500"
										title={col.cellTitle?.(item)}
									>
										{col.linkUrl ? (
											<a
												href={`${col.linkUrl(item)}`}
												className="text-gray-600 hover:text-blue-600 hover:underline cursor-pointer"
												title={col.cellTitle?.(item)}
											>
												{col.render(item)}
											</a>
										) : (
											col.render(item)
										)}
									</td>
								))}
								{actions.length > 0 && (
									<td className="px-6 py-4 border-b">
										<div className="flex gap-2 whitespace-nowrap justify-end">
											{actions.map((action, aidx) => {
												if (
													action.type === 'manage' ||
													action.type === 'edit'
												) {
													return (
														<MainButton
															key={aidx}
															label={action.label}
															onClick={() => action.onClick(item)}
															size={size}
															icon={faPen}
															hideIcon={hideIcons}
														/>
													);
												}
												if (action.type === 'delete') {
													return (
														<DeleteButton
															key={aidx}
															question={
																action.confirmMessage?.(item) || 'Supprimer ?'
															}
															onConfirm={() => action.onClick(item)}
															size={size}
															icon={faTrashAlt}
															hideIcon={hideIcons}
														/>
													);
												}
												return (
													<MainButton
														key={aidx}
														label={action.label}
														onClick={() => action.onClick(item)}
														size={size}
													/>
												);
											})}
										</div>
									</td>
								)}
							</tr>
						))
					)}
				</tbody>
			</table>
		</div>
	);
}

export default GenericTable;
